import React from 'react';
import { connect } from 'react-redux';

// Constellation Components
import {
    Heading,
    ModalDialog,
    Paragraph,
    Spacer,
    Text,
    Tooltip,
    TooltipPopper,
    TriggerText,
} from '@zillow/constellation';

import {
    Col,
    Container,
    Row,
} from '@zillow/zrs-comet';

import {
    TwoFactorAuth,
} from '@zg-rentals/particles-js-auth';

// Selectors
import {
    RoutingSelectors,
    UserSelectors,
} from 'app/shared/selectors';

// actions
import UserActions from 'app/shared/actions/UserActions';
import EmailVerificationActions from 'app/shared/actions/EmailVerificationActions';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// components
import PermissionCheck from 'app/shared/components/PermissionCheck';

// constants
import KeyCodeConstants from 'app/shared/constants/KeyCodeConstants';
import { SETTINGS_PAGE } from 'app/shared/constants/RouteConstants';

// styled components
import {
    StyledAnchor,
    StyledRow,
    StyledContentBox,
    StyledOnOffSwitch,
    StyledText,
} from './StyledComponents';

class SettingsPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            verificationEmailSent: false,
            shouldShowTwoFactorOnboarding: false,
            phoneNumber: null,
        };

        this.renderContent = this.renderContent.bind(this);
        this.focusSwitchRef = this.focusSwitchRef.bind(this);
    }

    handleVerifyEmail = () => {
        this.props.dispatch(EmailVerificationActions.sendSecurityToken())
            .then(() => {
                this.setState({
                    verificationEmailSent: true,
                });
            });
    };

    // function that runs when user clicks "Send verification code"
    // must return a Promise that resolves the following format: { success: Boolean }
    onRequestCode = ({ phoneNumber, contactType }) => {
        const { dispatch } = this.props;

        return dispatch(UserActions.enableTwoFactorAuth({
            phoneNumber,
            contactType,
        }));
    };

    onSubmitCode = ({ verificationCode }) => {
        const { dispatch } = this.props;

        return dispatch(UserActions.submitTwoFactorVerificationCode(verificationCode));
    };

    onSubmitCodeSuccess = () => {
        const { dispatch } = this.props;

        dispatch(UserActions.getUserAndSettings())
            .then(() => {
                this.setState({
                    shouldShowTwoFactorOnboarding: false,
                });
            });
    };

    handleTwoFactorToggle = () => {
        const {
            dispatch,
            isTwoFactorAuthEnabled,
        } = this.props;

        // is not activated, should onboard
        if (!isTwoFactorAuthEnabled) {
            this.setState({
                shouldShowTwoFactorOnboarding: true,
            });
        } else {
            // make api call to disable 2FA
            dispatch(UserActions.disableTwoFactorAuth())
                .then(() => dispatch(UserActions.getUserAndSettings()));
        }
    };

    getRegisteredPhoneNumber = () => {
        const { dispatch } = this.props;

        dispatch(UserActions.getAccountSettings())
            .then((data) => {
                this.setState({ phoneNumber: data.phoneNumber });
            });
    };

    onKeyPress = (event) => {
        if (event.keyCode === KeyCodeConstants.SPACE) {
            event.target.click();
        }
    }

    onKeyUp = (event) => {
        if (event.keyCode === KeyCodeConstants.ENTER ||
            event.keyCode === KeyCodeConstants.SPACE) {
            event.target.click();
        }
    }

    focusSwitchRef() {
        // Focus the switch ref using the raw DOM API
        // after closing the Modal Dialog
        if (this.switchRef) {
            this.switchRef.focus();
        }
    }

    componentDidMount() {
        this.getRegisteredPhoneNumber();
    }

    renderContent() {
        const {
            isEmailVerified,
            isTwoFactorAuthEnabled,
        } = this.props;

        const {
            shouldShowTwoFactorOnboarding,
            verificationEmailSent,
            phoneNumber,
        } = this.state;

        const tooltip = (
            <Tooltip
                shouldReturnFocus={true}
                shouldTakeFocus={true}
                shouldTrapFocus={true}
                body={(
                    <Paragraph>
                        To change your number, please turn off Two-factor Authentication. Once done,
                        re-enable to enter a new phone number.
                    </Paragraph>
                )}
            />
        );

        const tooltipTrigger = (
            <TooltipPopper triggered={tooltip}>
                <TriggerText>change number</TriggerText>
            </TooltipPopper>
        );

        return (
            <Container>
                <Spacer marginY="md">
                    <Row paddingY="xs">
                        <Col>
                            <Heading level={3} as="div">Settings Page</Heading>
                        </Col>
                    </Row>
                </Spacer>

                <StyledRow paddingY="xs">
                    <Col xs={12}>
                        <Row paddingY="xs">
                            <Col xs={12}>
                                <Text marginBottom="xs" fontType="h6" as="div">
                                    Two-factor Authentication
                                </Text>
                            </Col>
                        </Row>
                    </Col>

                    <Col xs={12}>
                        <Text marginBottom="sm" fontColor="textMedium" as="div">
                            Two factor authentication adds an additional level of security to your
                            account when using:
                        </Text>

                        <Spacer marginBottom="sm">
                            <ul>
                                <li><Text fontColor="textMedium">Applications</Text></li>
                                <li><Text fontColor="textMedium">Payments</Text></li>
                            </ul>
                        </Spacer>

                        <Text marginBottom="sm" fontColor="textMedium" as="div">
                            We’ll text or call your phone with a security code to verify your identity.
                        </Text>

                        {!isEmailVerified ? (
                            <div className="SettingsPage-VerifyEmail">
                                <Spacer marginBottom="lg">
                                    <StyledContentBox highlight="gray">
                                        {!verificationEmailSent ? (
                                            <React.Fragment>
                                                <Text>
                                                    You must verify your email before enabling two factor
                                                    authentication.&nbsp;
                                                    <StyledAnchor
                                                        aria-label="verify email"
                                                        tabIndex={0}
                                                        role="button"
                                                        onClick={this.handleVerifyEmail}
                                                        onKeyUp={this.onKeyUp}
                                                    >
                                                        verify email
                                                    </StyledAnchor>
                                                </Text>
                                            </React.Fragment>
                                        ) : (
                                            <React.Fragment>
                                                We’ve sent you an email, please check your inbox.
                                            </React.Fragment>
                                        )}
                                    </StyledContentBox>
                                </Spacer>
                            </div>
                        ) : (
                            <React.Fragment>
                                <StyledOnOffSwitch
                                    ref={(el) => { this.switchRef = el; }}
                                    disabled={false}
                                    on={this.props.isTwoFactorAuthEnabled}
                                    onClick={this.handleTwoFactorToggle}
                                    onKeyPress={this.onKeyPress}
                                />
                                <ModalDialog
                                    isOpen={shouldShowTwoFactorOnboarding}
                                    scrollDocument={true}
                                    wide={true}
                                    onClose={() => {
                                        this.setState({ shouldShowTwoFactorOnboarding: false });
                                        this.focusSwitchRef();
                                    }}
                                    body={(
                                        <Spacer marginX="xs">
                                            <TwoFactorAuth.Onboarding
                                                onRequestCode={this.onRequestCode}
                                                onSubmitCode={this.onSubmitCode}
                                                onSubmitCodeSuccess={this.onSubmitCodeSuccess}
                                            />
                                        </Spacer>
                                    )}
                                />
                            </React.Fragment>
                        )}

                        {isTwoFactorAuthEnabled && (
                            <Spacer marginY="md">
                                <Row paddingY="xs">
                                    <Col xs={12} md={3} xl={3} lg={3}>
                                        <StyledText className="ccnum fs-exclude" as="div">
                                            {phoneNumber}
                                        </StyledText>

                                        <StyledText fontColor="blue400" as="div">
                                            {tooltipTrigger}
                                            <TooltipPopper triggered={tooltip} />
                                        </StyledText>
                                    </Col>
                                </Row>
                            </Spacer>
                        )}
                    </Col>
                </StyledRow>

                <Spacer marginBottom="lg" />
            </Container>
        );
    }

    render() {
        const { user } = this.props;
        const { features } = user;
        const { canAccessMainSecuritySettings } = features;

        return (
            <AuthCheckWrapper requireStrongAuthentication={canAccessMainSecuritySettings}>
                <PermissionCheck route={SETTINGS_PAGE} isRedirectExternal={true}>
                    {this.renderContent()}
                </PermissionCheck>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
    isEmailVerified: UserSelectors.makeIsEmailVerified(state),
    isTwoFactorAuthEnabled: UserSelectors.makeIsTwoFactorAuthEnabled(state),
    routing: RoutingSelectors.makeRouting(state),
});

export { SettingsPage };
export default connect(mapStateToProps)(SettingsPage);
